// src/utils/pdfTemplate.js
// Gemeinsame jsPDF-Bausteine (Corporate Design), extrahiert aus
// src/services/costs/zahlungsanweisungPdf.js (ADR 0054), damit weitere
// PDF-Exporte (z.B. Sitzungsprotokoll, ADR 0062) dieselbe Vorlage nutzen
// statt sie zu duplizieren. Reine Extraktion — keine Wertaenderung gegenueber
// den bisherigen lokalen Konstanten/Funktionen in zahlungsanweisungPdf.js.

export const MF_FIRMA = "Malheur&Fortuna";
// Absenderzeile der Fusszeile — einzige Stelle, an der die Adresse steht.
export const MF_ABSENDER = "Malheur&Fortuna GmbH · Claragraben 103 · 4057 Basel";

export const LOGO_URL = "/MF_Logo_RGB_Black.png";
export const LOGO_RATIO = 478 / 994; // Hoehe / Breite der PNG-Datei

export const LEFT = 20;
export const RIGHT = 190;
export const LH = 4.3; // Zeilenhoehe in mm bei 9.5 pt

// Laedt das lokale Logo (public/) als Data-URL. Browser-only (FileReader).
export async function loadLogoDataUrl() {
  try {
    const res = await fetch(LOGO_URL);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// Laedt ein Bild von einer beliebigen (z.B. SAS-signierten Azure-Blob-) URL
// als Data-URL — Node- UND Browser-tauglich (kein FileReader). Base64 wird in
// Chunks kodiert, weil ein einzelnes String.fromCharCode.apply ueber ein
// komplettes ~300KB-JPEG-Byte-Array in manchen Engines einen RangeError
// werfen kann. Gibt bei jedem Fehler (Netzwerk, CORS, 404) null zurueck,
// statt zu werfen — ein einzelnes nicht ladbares Bild darf nie den gesamten
// PDF-Aufbau abbrechen; Aufrufer rendern den betroffenen Eintrag dann ohne Bild.
export async function loadImageAsDataUrl(url, mime = "image/jpeg") {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const buffer = await res.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    const chunkSize = 8192;
    let binary = "";
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
    }
    const base64 = typeof btoa === "function" ? btoa(binary) : Buffer.from(bytes).toString("base64");
    return `data:${mime};base64,${base64}`;
  } catch {
    return null;
  }
}

// Zeichen-Helfer als Factory (statt Closures direkt in einer Generator-
// Funktion) — so nutzen mehrere PDF-Generatoren fuer dieselbe jsPDF-Instanz
// dieselben Helfer, ohne sie je neu zu definieren.
export function createDrawHelpers(doc, { left = LEFT, right = RIGHT, lh = LH } = {}) {
  const font = (style = "normal", size = 9.5) => {
    doc.setFont("helvetica", style);
    doc.setFontSize(size);
  };
  const txt = (s, x, y, opts = {}) => {
    if (s == null || s === "") return;
    doc.text(String(s), x, y, opts);
  };
  const hr = (y, x1 = left, x2 = right, width = 0.3) => {
    doc.setLineWidth(width);
    doc.setDrawColor(60);
    doc.line(x1, y, x2, y);
  };
  // Mehrzeiligen Text ausgeben, gibt y der naechsten freien Zeile zurueck.
  const block = (lines, x, y, opts = {}) => {
    let yy = y;
    for (const line of lines) {
      txt(line, x, yy, opts);
      yy += lh;
    }
    return yy;
  };
  return { font, txt, hr, block };
}
